import React, { useEffect, useState, useRef, useMemo } from "react"
import { createPortal } from "react-dom"
import { Node, nodeInputRule } from "@tiptap/core"
import { ReactNodeViewRenderer, NodeViewWrapper } from "@tiptap/react"
import classNames from "classnames"
import { createPopper } from "@popperjs/core"
import KaTeX from "katex"
import { TrashIcon } from "@heroicons/react/24/solid"

import useOutsideClick from "../../../../../hooks/useOutsideClick"
import { Transaction } from "prosemirror-state"

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    mathBlock: {
      setMathBlock: () => ReturnType
      deleteMathBlockAt: (pos: number) => ReturnType
    }
  }
}

const Component = ({
  extension,
  updateAttributes,
  editor,
  node,
  getPos,
  selected,
}) => {
  const isBlock = extension.name === "mathBlock"

  const portalTargetRef = extension.options.targetRef
  const popupElementRef = useRef<HTMLDivElement | null>(null)
  const modalContainerRef = useOutsideClick(() => {
    closeAndSave()
    return true
  })

  const [editing, setEditing] = useState(extension.storage?.editNextMathBlock)
  if (extension.storage) extension.storage.editNextMathBlock = false

  const [latex, setLatex] = useState(node.attrs.latex || "")

  const closeAndSave = () => {
    setEditing(false)
    updateAttributes({ latex })
    editor.chain().focus().run()
  }

  const popupEditor = () => {
    if (!popupElementRef.current) return
    popupElementRef.current.classList.remove("hidden")
    const domElement = editor.view.nodeDOM(getPos())
    createPopper(domElement, popupElementRef.current, {
      placement: "bottom-start",
      modifiers: [
        {
          name: "offset",
          options: {
            offset: [0, 5],
          },
        },
      ],
    })
  }

  const popdownEditor = () => {
    if (!popupElementRef.current) return
    popupElementRef.current.classList.add("hidden")
  }

  const inputRef = useRef<HTMLTextAreaElement>(null)

  // This can happen when the editor does undo/redo
  useEffect(() => {
    setLatex(node.attrs.latex || "")
  }, [node.attrs.latex])

  // Grab focus whenever we render, if there is an input.
  useEffect(() => {
    setTimeout(() => inputRef.current?.focus(), 50)
  })

  // Whenever editing state changes, either popup the editor, or close it
  useEffect(() => {
    if (!popupElementRef.current) return

    if (editing) {
      // When editing goes => true on insert of a math block, this is run *before* the node is
      // rendered into the editor, so we have to wait for that.
      setTimeout(() => popupEditor(), 1)
    } else {
      popdownEditor()
    }
  }, [editing])

  const { html, error } = useMemo(() => {
    try {
      const html = KaTeX.renderToString(latex, {
        displayMode: isBlock,
        throwOnError: true,
      })

      return { html, error: undefined }
    } catch (error) {
      if (error instanceof KaTeX.ParseError) {
        // Rerun Katex rendering to get the nice coloured output - there's no option to
        // get that and the error at the same time so we have to run it twice.
        const html = KaTeX.renderToString(latex, {
          displayMode: isBlock,
          errorColor: "#DD6666",
          throwOnError: false,
        })
        return { html, error }
      }
      throw error
    }
  }, [latex])

  const selectionClasses = "border-2 dark:border-sky-800 -mb-1"

  const editAndSelect = () => {
    setEditing(true)
    editor.commands.setNodeSelection(getPos())
  }

  return (
    <NodeViewWrapper className={classNames({ "inline-block": !isBlock })}>
      <div
        className={classNames(
          "cursor-pointer overflow-visible group relative",
          {
            "inline-block": !isBlock,
          }
        )}
      >
        {latex === "" ? (
          <div
            onClick={() => editAndSelect()}
            className={classNames(
              "text-gray-300 dark:text-gray-600 text-sm min-h-8 p-2 flex justify-center dark:hover:bg-gray-700 rounded cursor-pointer",
              {
                [selectionClasses]: selected,
              }
            )}
          >
            Empty equation block
          </div>
        ) : (
          <div
            onClick={() => editAndSelect()}
            className={classNames(
              "hover:bg-gray-100 dark:hover:bg-gray-700 rounded",
              {
                [selectionClasses]: selected,
                "min-h-8": isBlock,
              }
            )}
            dangerouslySetInnerHTML={{ __html: html }}
          ></div>
        )}
        {isBlock && (
          <div
            className="hidden group-hover:block absolute top-0 right-0 mt-2 mr-2 text-gray-400 dark:text-gray-400 dark:hover:text-red-500"
            onClick={() => {
              editor.commands.deleteMathBlockAt(getPos())
            }}
          >
            <TrashIcon className="w-3 h-3" />
          </div>
        )}
      </div>
      {editing &&
        createPortal(
          <div ref={popupElementRef}>
            <div
              ref={modalContainerRef}
              className="w-72 md:w-120 flex flex-col items-stretch rounded overflow-hidden bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border dark:border-gray-600 dark:text-gray-400 shadow"
            >
              <div className="p-2 flex items-start justify-between w-full border-b border-gray-200">
                <div
                  className="flex-grow grow-wrap-tex-editor "
                  data-replicated-value={latex}
                >
                  <textarea
                    ref={inputRef}
                    value={latex}
                    onChange={(e) => {
                      setLatex(e.target.value)
                    }}
                    className="w-full p-0 font-mono font-medium text-sm resize-none bg-gray-50 dark:bg-gray-700 text-gray-800 dark:text-gray-200 border-0 outline-none focus:outline-none focus:ring-0"
                    placeholder="A TeX expression"
                    onKeyDown={(e) => {
                      if (
                        e.key == "Escape" ||
                        (e.key == "Enter" && e.metaKey)
                      ) {
                        e.preventDefault()
                        closeAndSave()
                      }
                    }}
                    rows={isBlock ? 3 : 1}
                  />
                </div>
                <div className="flex gap-4 text-sm">
                  <div
                    onClick={() => {
                      closeAndSave()
                    }}
                    className={classNames(
                      "px-2 py-1 rounded  font-semibold text-xs bg-sky-500 text-white cursor-pointer",
                      {}
                    )}
                  >
                    Done
                  </div>
                </div>
              </div>

              {error ? (
                <div className="p-2 text-red-800 dark:text-red-400 bg-white dark:bg-gray-800 text-xs ">
                  {error.message}
                </div>
              ) : null}
            </div>
          </div>,
          portalTargetRef.current
        )}
    </NodeViewWrapper>
  )
}

const MathInline = Node.create({
  name: "mathInline",
  group: "inline",
  atom: true,
  inline: true,
  selectable: true,

  parseHTML() {
    return [
      {
        tag: "math-inline",
      },
    ]
  },

  addAttributes() {
    return {
      latex: {
        parseHTML: (element) => element.getAttribute("latex"),
      },
    }
  },

  renderHTML({ HTMLAttributes }) {
    return ["math-inline", HTMLAttributes]
  },

  addNodeView() {
    return ReactNodeViewRenderer(Component)
  },

  addInputRules() {
    return [
      nodeInputRule({
        find: /(?:^|\s)(\$([^\$]+)\$)$/,
        type: this.type,
        getAttributes: (match) => {
          return {
            latex: match[2],
          }
        },
      }),
    ]
  },
})

const MathBlock = Node.create({
  name: "mathBlock",
  group: "block",
  atom: true,

  addStorage() {
    return {
      editNextMathBlock: false,
    }
  },

  addCommands() {
    return {
      setMathBlock:
        () =>
        ({ commands }) => {
          this.storage.editNextMathBlock = true
          return commands.insertContent('<math-block latex=""></math-block>')
        },
      deleteMathBlockAt:
        (pos: number) =>
        ({ tr }: { tr: Transaction }) => {
          tr.delete(pos, pos + 1)
          return true
        },
    }
  },

  parseHTML() {
    return [
      {
        tag: "math-block",
      },
    ]
  },

  addAttributes() {
    return {
      latex: {
        parseHTML: (element) => element.getAttribute("latex"),
      },
    }
  },

  renderHTML({ HTMLAttributes }) {
    return ["math-block", HTMLAttributes]
  },

  addNodeView() {
    return ReactNodeViewRenderer(Component)
  },
})

export { MathBlock, MathInline }
